import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';

// ── Types — aligned with actual API responses ──

export interface FaceAIStatus {
  status: string;
  module: string;
  capabilities: string[];
  detector_backend: string;
  encoder_model: string;
  gallery_size: number;
  index_vectors: number;
  db_stats: Record<string, number>;
  biometric_comparator: boolean;
}

export interface DetectedFaceResult {
  face_index: number;
  bbox: number[];
  confidence: number;
  analysis: Record<string, unknown>;
  face_thumbnail?: string;
}

export interface DetectResponse {
  faces_detected: number;
  results: DetectedFaceResult[];
  processing_time_ms: number;
}

export interface RecognizeResult {
  face_index: number;
  bbox: number[];
  detection_confidence: number;
  person_name: string;
  recognition_confidence: number;
  analysis: Record<string, unknown>;
}

export interface RecognizeResponse {
  faces_detected: number;
  results: RecognizeResult[];
  processing_time_ms: number;
}

export interface EnrollResponse {
  user_id: number;
  name: string;
  images_processed: number;
  encodings_created: number;
}

export interface CompareResponse {
  is_match: boolean;
  similarity: number;
  threshold: number;
  face_a_detected: boolean;
  face_b_detected: boolean;
  biometric_detail: unknown;
  processing_time_ms: number;
}

export interface SearchMatch {
  rank: number;
  person_name: string;
  confidence: number;
  image_path: string;
}

export interface SearchResponse {
  matches_found: number;
  matches: SearchMatch[];
  search_time_ms: number;
}

export interface UsersResponse {
  users: Array<Record<string, unknown>>;
  total: number;
  gallery_names: string[];
}

export interface AnalyzeResult {
  bbox: number[];
  confidence: number;
  age: number;
  gender: string;
  gender_confidence: number;
  dominant_emotion: string;
  emotions: Record<string, number>;
  gaze_direction: string;
  face_quality: number;
}

export interface AnalyzeResponse {
  faces_found: number;
  analyses: AnalyzeResult[];
}

export interface HistoryRecord {
  id: number;
  user_id: number | null;
  recognized_name: string;
  confidence: number;
  source: string;
  image_path: string;
  analysis_json: string;
  timestamp: string;
}

export interface HistoryStats {
  total_recognitions: number;
  today: number;
  avg_confidence: number;
  top_person: { recognized_name: string; cnt: number } | null;
}

export interface HistoryResponse {
  records: HistoryRecord[];
  stats: HistoryStats;
}

export interface OsintAnalyzeResponse {
  faces: Array<{
    bbox: number[];
    confidence: number;
    analysis: Record<string, unknown>;
    has_encoding: boolean;
  }>;
  faces_count: number;
  osint_metadata: Record<string, unknown>;
  processing_time_ms: number;
}

export interface FaceAIStats {
  users: number;
  recognitions: number;
  searches: number;
  osint_sources: number;
  osint_entities: number;
  active_alerts: number;
  experiments: number;
  video_sessions: number;
  index: {
    total_vectors: number;
    dimension: number;
    index_type: string;
    faiss_available: boolean;
    metadata_entries: number;
  };
  gallery_size: number;
}

// ── API Functions ──

async function fetchStatus(): Promise<FaceAIStatus> {
  const { data } = await apiClient.get('/faceai/status');
  return data;
}

async function detectFaces(file: File): Promise<DetectResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await apiClient.post('/faceai/detect', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

async function recognizeFaces(file: File): Promise<RecognizeResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await apiClient.post('/faceai/recognize', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

async function enrollFace(files: File[], name: string): Promise<EnrollResponse> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('images', file);
  }
  const { data } = await apiClient.post(`/faceai/enroll?name=${encodeURIComponent(name)}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

async function compareFaces(fileA: File, fileB: File): Promise<CompareResponse> {
  const formData = new FormData();
  formData.append('face_a', fileA);
  formData.append('face_b', fileB);
  const { data } = await apiClient.post('/faceai/compare', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

async function fetchUsers(): Promise<UsersResponse> {
  const { data } = await apiClient.get('/faceai/users');
  return data;
}

async function searchFace(file: File): Promise<SearchResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await apiClient.post('/faceai/search', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

async function fetchStats(): Promise<FaceAIStats> {
  const { data } = await apiClient.get('/faceai/stats');
  return data;
}

async function analyzeFace(file: File): Promise<AnalyzeResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await apiClient.post('/faceai/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

async function fetchHistory(limit: number = 50): Promise<HistoryResponse> {
  const { data } = await apiClient.get('/faceai/history', { params: { limit } });
  return data;
}

async function deleteUser(userId: number): Promise<void> {
  await apiClient.delete(`/faceai/users/${userId}`);
}

async function osintAnalyze(file: File): Promise<OsintAnalyzeResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await apiClient.post('/faceai/osint-analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ── Full Identity Pipeline (Face → OSINT → Graph) ──

export interface ReverseSearchResult {
  title: string;
  url: string;
  source: string;
  confidence?: number;
}

export interface ReverseImageSearchResponse {
  best_guess: string;
  entity_name: string;
  results: ReverseSearchResult[];
  engines_searched: string[];
  engines_failed: string[];
  total_results: number;
  search_time_ms: number;
  identity: Record<string, unknown>;
  digital_life: Record<string, unknown>;
}

export interface FullIdentityResponse {
  faces: Array<{
    bbox: number[];
    confidence: number;
    analysis: Record<string, unknown>;
    has_encoding: boolean;
  }>;
  faces_count: number;
  osint_metadata: Record<string, unknown>;
  processing_time_ms: number;
  reverse_search?: ReverseImageSearchResponse;
  gallery_matches?: Array<{ person_name: string; confidence: number }>;
  liveness?: { verdict: string; score: number; details: Record<string, unknown> };
}

async function reverseImageSearch(file: File): Promise<ReverseImageSearchResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await apiClient.post('/osint-advanced/reverse-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return data;
}

async function fullIdentityPipeline(file: File): Promise<FullIdentityResponse> {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await apiClient.post('/faceai/osint-analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return data;
}

// ── React Query Hooks ──

export function useFaceAIStatus() {
  return useQuery({
    queryKey: ['faceai-status'],
    queryFn: fetchStatus,
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: 1,
  });
}

export function useDetectFaces() {
  return useMutation({ mutationFn: detectFaces });
}

export function useRecognizeFaces() {
  return useMutation({ mutationFn: recognizeFaces });
}

export function useEnrollFace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ files, name }: { files: File[]; name: string }) => enrollFace(files, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faceai-users'] });
      queryClient.invalidateQueries({ queryKey: ['faceai-stats'] });
      queryClient.invalidateQueries({ queryKey: ['faceai-status'] });
    },
  });
}

export function useCompareFaces() {
  return useMutation({
    mutationFn: ({ fileA, fileB }: { fileA: File; fileB: File }) => compareFaces(fileA, fileB),
  });
}

export function useFaceAIUsers() {
  return useQuery({
    queryKey: ['faceai-users'],
    queryFn: fetchUsers,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useSearchFace() {
  return useMutation({ mutationFn: searchFace });
}

export function useFaceAIStats() {
  return useQuery({
    queryKey: ['faceai-stats'],
    queryFn: fetchStats,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useAnalyzeFace() {
  return useMutation({ mutationFn: analyzeFace });
}

export function useFaceAIHistory(limit: number = 50) {
  return useQuery({
    queryKey: ['faceai-history', limit],
    queryFn: () => fetchHistory(limit),
    staleTime: 15_000,
    retry: 1,
  });
}

export function useDeleteFaceAIUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faceai-users'] });
      queryClient.invalidateQueries({ queryKey: ['faceai-stats'] });
      queryClient.invalidateQueries({ queryKey: ['faceai-status'] });
    },
  });
}

export function useOsintAnalyze() {
  return useMutation({ mutationFn: osintAnalyze });
}

export function useReverseImageSearch() {
  return useMutation({ mutationFn: reverseImageSearch });
}

export function useFullIdentityPipeline() {
  return useMutation({ mutationFn: fullIdentityPipeline });
}
